import { Call, Device } from '@twilio/voice-sdk';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { VoiceStatus, VoiceToken } from 'server-sdk/types';
import { useReduxState } from 'ui-sdk/src/hooks';
import { call } from 'ui-sdk/src/call';
import { api } from 'server-sdk/api';

export const useVoice = (
  fromPhone: string,
  toPhone: string,
  isEnabled: boolean,
) => {
  const dispatch = useDispatch();
  const user = useReduxState((state) => state.identity.user);

  const [device, setDevice] = useState(undefined);
  const [tokenIdentity, setTokenIdentity] = useState<VoiceToken>(undefined);
  const [currentCall, setCurrentCall] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(VoiceStatus.notReady);
  const getToken = () => {
    dispatch(async () => {
      if (!isLoading && isEnabled) {
        console.log('getting voice token');
        setIsLoading(true);
        const t = await call(api.inbox.voice.getToken, {});
        initializeDevice(t.token);
        // setIsLoading(false); // only need to load token once?
        if (t) {
          setTokenIdentity(t);
        }
      } else {
        console.log('skip chat call');
      }
    });
  };

  const initializeDevice = (token) => {
    const newDevice = new Device(token, {
      logLevel: 1,
      // Set Opus as our preferred codec. Opus generally performs better, requiring less bandwidth and
      // providing better audio quality in restrained network conditions.
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
    });

    addDeviceListeners(newDevice);

    // Device must be registered in order to receive incoming calls
    newDevice.register();

    setDevice(newDevice);
  };

  const addDeviceListeners = (device) => {
    device.on('registered', () => {
      setStatus(VoiceStatus.ready);
    });

    device.on('error', (error) => {
      console.error(error);
      setStatus(VoiceStatus.error);
    });

    device.on('incoming', handleIncomingCall);
  };

  const handleIncomingCall = (call) => {
    // add event listener to call object
    call.on('cancel', updateUIDisconnectedCall);
    call.on('disconnect', updateUIDisconnectedCall);
    call.on('reject', updateUIDisconnectedCall);
    setCurrentCall(call);
    setStatus(VoiceStatus.incomingCall);
  };

  const acceptIncomingCall = () => {
    currentCall.accept();
    setStatus(VoiceStatus.callAccepted);
  };

  // REJECT INCOMING CALL

  const rejectIncomingCall = () => {
    currentCall.reject();
    setStatus(VoiceStatus.callDisconnected);
  };

  const makeOutgoingCall = async () => {
    const params = {
      // get the phone number to call from the DOM
      To: toPhone,
      Identity: tokenIdentity.identity,
      CallerId: fromPhone,
    };

    if (device) {
      setStatus(VoiceStatus.calling);
      // Twilio.Device.connect() returns a Call object
      const call = await device.connect({ params });

      // add listeners to the Call
      // "accepted" means the call has finished connecting and the state is now "open"
      call.on('accept', updateUIAcceptedOutgoingCall);
      call.on('disconnect', updateUIDisconnectedCall);
      call.on('cancel', updateUIDisconnectedCall);

      setCurrentCall(call);
    }
  };

  const updateUIAcceptedOutgoingCall = () => {
    console.log('ACCEPTED');
    setStatus(VoiceStatus.callAccepted);
  };

  const updateUIDisconnectedCall = () => {
    setStatus(VoiceStatus.callDisconnected);
  };

  const hangUpCall = () => {
    currentCall.disconnect();
    setCurrentCall(undefined);
    setStatus(VoiceStatus.callDisconnected);
  };

  useEffect(() => {
    getToken();
  }, [user, isEnabled]);

  return {
    tokenIdentity,
    status,
    currentCall,
    makeOutgoingCall,
    hangUpCall,
    acceptIncomingCall,
    rejectIncomingCall,
  };
};
